"use client";

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { ClientLogger } from "@/lib/clientLogger";
import { apiAuthFetch } from "@/lib/authFetch";

/**
 * Per-thread scope mirror (v7+). The composer drives this via the CasePill +
 * ComposerAddMenu affordances; elicit-approvals on out-of-scope tool calls
 * also persist through here. The server is the single source of truth — this
 * context just holds the latest snapshot read from the thread row so the
 * composer can render the right pills and the elicitation card can optimistic-
 * update before the next thread fetch.
 *
 * Removed in v7:
 *  - basePolicy + accumulatedApprovals + toWireFormat — the server no longer
 *    accepts a `scopePolicy` on the chat request; it hydrates from the thread
 *    row via ScopePolicyHydrator.FromThread().
 *  - scopeOverride / setScopeOverride — the "Celý Praetor" / "Bez přístupu"
 *    overrides were retired with the composer scope chip. "no access" is now
 *    expressed by deselecting praetorCases from the MCP toggle list; "allow
 *    all reads" is gone (the new mental model is "selection IS scope").
 *  - addApprovedDoc — elicitation only widens at case granularity now.
 *  - loadDevOverride + DEV_OVERRIDE_KEY — dev escape hatch tied to the
 *    removed override flow; redundant once selection-is-scope lands.
 */

/** Thread scope fields as returned by `GET /api/threads/{id}` and `GET /api/threads`. */
export type ThreadScopeFields = {
  mainCaseId?: string | null;
  mainCaseDocumentIds?: string[] | null;
  additionalCaseIds?: string[] | null;
  additionalDocumentsByCase?: Record<string, string[]> | null;
};

type ScopePolicyContextValue = {
  /** Thread scope fields from the backend — drives the composer's pill rendering. */
  threadScope: ThreadScopeFields;
  /** Persist a case-add through POST /api/threads/{id}/scope. Optimistic local update. */
  addApprovedCase: (caseId: string) => void;
  /**
   * Drop a case from <c>additionalCaseIds</c>. Optimistic. The thread's <c>mainCaseId</c>
   * is rejected by the server with HTTP 400 — callers must gate "is this the main case?"
   * before invoking this method (the composer hides the X on the main pill).
   */
  removeApprovedCase: (caseId: string) => void;
  setThreadId: (threadId: string | undefined) => void;
  /** Seed scope fields from a backend thread response (called after thread load/create). */
  hydrateFromThread: (fields: ThreadScopeFields) => void;
};

const EMPTY_SCOPE: ThreadScopeFields = {};

const ScopePolicyContext = createContext<ScopePolicyContextValue>({
  threadScope: EMPTY_SCOPE,
  addApprovedCase: () => {},
  removeApprovedCase: () => {},
  setThreadId: () => {},
  hydrateFromThread: () => {},
});

export function ScopePolicyProvider({ children }: { children: ReactNode }) {
  const [, setThreadIdState] = useState<string | undefined>();
  const [threadScope, setThreadScope] = useState<ThreadScopeFields>(EMPTY_SCOPE);

  const threadIdRef = useRef<string | undefined>(undefined);

  const setThreadId = useCallback((id: string | undefined) => {
    threadIdRef.current = id;
    setThreadIdState(id);
    setThreadScope(EMPTY_SCOPE);
  }, []);

  const hydrateFromThread = useCallback((fields: ThreadScopeFields) => {
    setThreadScope(fields);
  }, []);

  const addApprovedCase = useCallback((caseId: string) => {
    if (!caseId) return;

    // Optimistic. If mainCaseId is still null, the server will promote this case
    // to main — mirror that locally so the composer renders it as the primary
    // pill on the next render. The next thread-row fetch corrects any divergence.
    setThreadScope((prev) => {
      if (!prev.mainCaseId) {
        return { ...prev, mainCaseId: caseId };
      }
      if (prev.mainCaseId === caseId) return prev;
      const existing = prev.additionalCaseIds ?? [];
      if (existing.includes(caseId)) return prev;
      return { ...prev, additionalCaseIds: [...existing, caseId] };
    });

    const tid = threadIdRef.current;
    if (tid) {
      apiAuthFetch(`api/threads/${tid}/scope`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ addCaseIds: [caseId] }),
      }).catch((err) => {
        ClientLogger.warn("Scope persist (case) failed", {
          component: "ScopePolicyContext",
          metadata: { threadId: tid, caseId, error: err },
        });
      });
    }
  }, []);

  const removeApprovedCase = useCallback((caseId: string) => {
    if (!caseId) return;
    setThreadScope((prev) => {
      const existingCases = prev.additionalCaseIds ?? [];
      const existingDocs = prev.additionalDocumentsByCase ?? {};
      const { [caseId]: _dropped, ...remainingDocs } = existingDocs;
      return {
        ...prev,
        additionalCaseIds: existingCases.filter(c => c !== caseId),
        additionalDocumentsByCase: remainingDocs,
      };
    });

    const tid = threadIdRef.current;
    if (tid) {
      apiAuthFetch(`api/threads/${tid}/scope`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ removeCaseIds: [caseId] }),
      }).catch((err) => {
        ClientLogger.warn("Scope un-persist (case) failed", {
          component: "ScopePolicyContext",
          metadata: { threadId: tid, caseId, error: err },
        });
      });
    }
  }, []);

  return (
    <ScopePolicyContext.Provider value={{
      threadScope,
      addApprovedCase,
      removeApprovedCase,
      setThreadId,
      hydrateFromThread,
    }}>
      {children}
    </ScopePolicyContext.Provider>
  );
}

export function useScopePolicy() {
  return useContext(ScopePolicyContext);
}
